import React, { useState } from 'react'
import { Box, Container, Typography, Collapse, Card, CardContent } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const FeaturedServices = () => {
  const theme = useTheme()
  const [expandedService, setExpandedService] = useState(null)

  const services = [
    {
      id: 'directory',
      title: 'Hospital Recruiter Directory',
      description: 'Verified contact information for in-house recruiters, hiring managers, and key personnel at hospitals and health systems nationwide.',
      details: 'Browse by location, health system, or specialty to find the right person to talk to. Every contact is an in-house recruiter — no third-party agencies.',
    },
    {
      id: 'salary',
      title: 'Salary Maps by Specialty',
      description: 'Interactive salary data showing compensation averages by medical specialty and geography across the United States.',
      details: 'Compare salaries across states, regions, and metropolitan areas. Understand where your specialty pays the most and factor compensation into your next career move.',
    },
    {
      id: 'application-tracking',
      title: 'Job Application Tracker',
      description: 'Keep your entire job search organized in one place — applications, contacts, notes, and offer details.',
      details: 'Track application status, save recruiter contact info, and keep notes on each position. No more scattered spreadsheets or scraps of paper.',
    },
  ]

  const handleServiceClick = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId)
  }

  return (
    <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.grey[900],
              fontWeight: 600,
              fontSize: '0.9rem',
              letterSpacing: '0.1em',
              mb: 2,
              display: 'block',
            }}
          >
            EXPLORE THE PLATFORM
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: theme.palette.grey[900],
              fontSize: { xs: '2rem', md: '2.5rem' },
              lineHeight: 1.2,
            }}
          >
            Tools That Go Beyond the Job Listing
          </Typography>
        </Box>

        <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
          {services.map((service) => (
            <Box key={service.id} sx={{ mb: 1 }}>
              <Box
                onClick={() => handleServiceClick(service.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  px: 0,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 400,
                    color: theme.palette.grey[900],
                    fontSize: '1rem',
                    opacity: 0.8,
                  }}
                >
                  {service.title}
                </Typography>

                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {expandedService === service.id ? '\u2212' : '+'}
                </Box>
              </Box>

              <Collapse in={expandedService === service.id}>
                <Card
                  sx={{
                    mt: 1,
                    mb: 3,
                    backgroundColor: 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '12px',
                    boxShadow: theme.palette.shadows.light,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        mb: 2,
                      }}
                    >
                      {service.description}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[900],
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        opacity: 0.8,
                      }}
                    >
                      {service.details}
                    </Typography>
                  </CardContent>
                </Card>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  )
}

export default FeaturedServices
